import {Component, OnDestroy, OnInit} from '@angular/core';
import {
	ConfirmPopupComponent,
	EQOBorder,
	EQOButtonType,
	EQOIcon,
	EQOLogLevel, EQORebrandColor,
	EQORebrandIcon,
	EQOShadow,
	QOLogService
} from '@quickorder/angular-web-lib';
import {QMIcon} from '@app/core/interfaces/q-m-icon.enum';
import {IQODropDownAction} from '@quickorder/angular-web-lib/lib/types/interfaces/q-o-drop-down-action.interface';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {Observable, Subscription} from 'rxjs';
import {selectPeriodType, selectSelectedDates, selectViewType} from '@app/core/store/selectors/schedule.selectors';
import {DeleteShiftsInInterval, SetPeriodType, SetPrinting, SetSelectedStartWeek, SetViewType} from '@app/core/store/actions/schedule.actions';
import {take} from 'rxjs/operators';
import * as moment from 'moment';
import {Moment} from 'moment';
import {Shift} from '@app/core/models/shift.model';
import {CreateShiftDraft} from '@app/core/store/actions/shift-drafts.actions';
import {selectShiftDrafts} from '@app/core/store/selectors/shift-drafts.selectors';
import {getCurrentState} from '@app/core/helpers';
import {MatDialog} from '@angular/material/dialog';
import {ShiftDraft} from '@app/core/models/ShiftDraft.model';
import {SelectShiftDraftPopupComponent} from '@app/core/popups/select-shift-draft-popup/select-shift-draft-popup.component';
import {selectCopyingWeek, selectShiftTemplates} from '@app/core/store/selectors/shift-templates.selectors';
import {CreateShiftTemplate, PostCopyingWeek, SetCopyingWeek} from '@app/core/store/actions/shift-templates.actions';
import {SelectShiftTemplatePopupComponent} from '@app/core/popups/select-shift-template-popup/select-shift-template-popup.component';
import {ShiftTemplate} from '@app/core/models/shift-template.model';
import {User} from '@app/core/models/user.model';
import {selectUser} from '@app/core/store/selectors/session.selectors';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import * as printJS from 'print-js';

export enum VIEW_TYPE {
	PERSON_VIEW = 0,
	GROUP_VIEW = 1,
	FUNCTION_VIEW = 2,
}

export enum PERIOD_TYPE {
	ONE_WEEK = 7,
	TWO_WEEKS = 14,
}

@Component({
	selector: 'app-planning-controls',
	templateUrl: './planning-controls.component.html',
	styleUrls: ['./planning-controls.component.scss'],
})
export class PlanningControlsComponent implements OnInit, OnDestroy {
	public ICONS = EQOIcon;
	public BORDERS = EQOBorder;
	public SHADOWS = EQOShadow;
	public QMICONS = QMIcon;
	public VIEW_TYPE = VIEW_TYPE;
	public PERIOD_TYPE = PERIOD_TYPE;
	public EQORebrandIcon: typeof EQORebrandIcon = EQORebrandIcon;
	public EQORebrandColor: typeof EQORebrandColor = EQORebrandColor;

	public viewType$: Observable<VIEW_TYPE> = this.store.select(selectViewType);
	public periodType$: Observable<PERIOD_TYPE> = this.store.select(selectPeriodType);
	public selectedDates$: Observable<Moment[]> = this.store.select(selectSelectedDates);
	public shiftDrafts$: Observable<ShiftDraft[]> = this.store.select(selectShiftDrafts);
	public shiftTemplates$: Observable<ShiftTemplate[]> = this.store.select(selectShiftTemplates);
	public copyingWeek$: Observable<Shift[]> = this.store.select(selectCopyingWeek);

	public user$: Observable<User> = this.store.select(selectUser);

	public firstDate: Moment;
	public lastDate: Moment;

	public toolActions: IQODropDownAction<any>[];
	public templateActions: IQODropDownAction<any>[];

	public _subscriptions: Subscription[] = [];

	constructor(
		private store: Store<IAppState>,
		private dialog: MatDialog,
		private interService: InternationalizationService,
		private logService: QOLogService,
	) {
		this.deleteWeek = this.deleteWeek.bind(this);
		this.copyWeek = this.copyWeek.bind(this);
		this.print = this.print.bind(this);
		this.pasteWeek = this.pasteWeek.bind(this);
		this.saveAsTemplate = this.saveAsTemplate.bind(this);
		this.chooseTemplate = this.chooseTemplate.bind(this);
		this.chooseShiftDraft = this.chooseShiftDraft.bind(this);
		this.createShiftDraft = this.createShiftDraft.bind(this);
		this._subscriptions.push(
			this.selectedDates$.subscribe(selectedDates => {
				this.firstDate = selectedDates[0];
				this.lastDate = selectedDates[selectedDates.length - 1];
			}),
			this.shiftDrafts$.subscribe(() => this.setActions()),
			this.copyingWeek$.subscribe(() => this.setActions()),
			this.shiftTemplates$.subscribe(() => this.setActions()),
			this.periodType$.subscribe(() => this.setActions()),
		);
	}

	public setActions(): void {
		this.toolActions = [
			{
				text: this.interService.translate.instant('SCHEDULE.COPY_WEEK'),
				onClick: this.copyWeek,
			},
			{
				text: this.interService.translate.instant('SCHEDULE.PASTE_WEEK'),
				onClick: this.pasteWeek,
				disabled: getCurrentState(this.store).shiftTemplates.copyingWeek.length === 0,
			},
			{
				text: this.interService.translate.instant('SCHEDULE.DELETE_SHIFTS_IN_PERIOD'),
				onClick: this.deleteWeek,
			},
			{
				text: this.interService.translate.instant('GLOBAL.PRINT'),
				onClick: this.print,
				disabled: getCurrentState(this.store).schedule.periodType !== PERIOD_TYPE.ONE_WEEK,
			},
			{
				text: this.interService.translate.instant('SCHEDULE.CREATE_NEW_SHIFT_DRAFT'),
				onClick: this.createShiftDraft,
			},
			{
				text: this.interService.translate.instant('SCHEDULE.CHOOSE_DRAFT'),
				onClick: this.chooseShiftDraft,
				disabled: getCurrentState(this.store).shiftDrafts.shiftDrafts.length === 0,
			},
		];
		this.templateActions = [
			/*			{
			 text: this.interService.translate.instant('SCHEDULE.CREATE_NEW_SHIFT_TEMPLATE'),
			 onClick: this.createNewTemplate
			 },*/
			{
				text: this.interService.translate.instant('SCHEDULE.SAVE_AS_TEMPLATE'),
				onClick: this.saveAsTemplate,
			},
			{
				text: this.interService.translate.instant('SCHEDULE.CHOOSE_TEMPLATE'),
				onClick: this.chooseTemplate,
				disabled: getCurrentState(this.store).shiftTemplates.shiftTemplates.length === 0,
			},
		];
	}

	public async deleteWeek(): Promise<void> {
		const ref = this.dialog.open(ConfirmPopupComponent, {
			data: {
				title: this.interService.translate.instant('GLOBAL.CONFIRM'),
				description: this.interService.translate.instant('SCHEDULE.CONFIRM_SHIFTS_DELETION'),
				rightButtonType: EQOButtonType.BIG_RED,
				leftButtonText: this.interService.translate.instant('GLOBAL.CLOSE'),
				rightButtonText: this.interService.translate.instant('GLOBAL.DELETE'),
			},
		});
		const response: { cancelled: boolean, value: string } = await ref.afterClosed().toPromise();
		if (!response || response.cancelled) {
			return;
		}
		const dates = getCurrentState(this.store).schedule.selectedDates;
		this.store.dispatch(new DeleteShiftsInInterval(dates[0], dates[dates.length - 1]));
	}

	public setViewType($event: VIEW_TYPE): void {
		this.store.dispatch(new SetViewType($event));
	}

	public setPeriodType($event: PERIOD_TYPE): void {
		this.store.dispatch(new SetPeriodType($event));
	}

	public async goToDate(date: Date): Promise<void> {
		const mDate = moment(date);
		this.store.dispatch(new SetSelectedStartWeek(mDate));
	}

	public async nextPeriod(): Promise<void> {
		const selectedDates = await this.selectedDates$.pipe(take(1)).toPromise();
		const nextMonday = selectedDates[selectedDates.length - 1].add(1, 'days');
		this.goToDate(nextMonday.toDate());
	}

	public async lastPeriod(): Promise<void> {
		const selectedDates = await this.selectedDates$.pipe(take(1)).toPromise();
		const lastMonday = selectedDates[0].subtract(selectedDates.length, 'days');
		this.goToDate(lastMonday.toDate());
	}

	public getWeekObject(): any[] {
		const selectedDates = getCurrentState(this.store).schedule.selectedDates;
		const shifts = getCurrentState(this.store).schedule.shifts;
		const dateToIndex = {};
		selectedDates.forEach((date, index) => {
			dateToIndex[date.format('YYYY-MM-DD')] = index;
		});
		const week = [];
		shifts.forEach(shift => {
			const dateIndex = dateToIndex[shift.start.localDate.format('YYYY-MM-DD')];
			const startTime = shift.start.localDate.format('HH:mm');
			const endTime = shift.end.localDate.format('HH:mm');
			if (startTime === endTime) {
				this.logService.logContent({
					level: EQOLogLevel.INFO,
					title: this.interService.translate.instant('SNACKBAR.ERROR'),
					actions: [],
					description: this.interService.translate.instant('SCHEDULE.SAME_START_END_HAS_NOT_BEEN_COPIED'),
				});
			}
			if (dateIndex !== undefined && startTime !== endTime) {
				week.push({
					group_id: shift.group.id,
					user_id: shift.user.id,
					pause: shift.pause,
					start_date_index: dateIndex,
					start_time: startTime,
					end_time: endTime,
					note: shift.note,
				});
			}
		});

		return week;
	}

	public async copyWeek(): Promise<void> {
		const copyingWeek = await this.getWeekObject();
		this.store.dispatch(new SetCopyingWeek(copyingWeek));
	}

	public async pasteWeek(): Promise<void> {
		const copyingWeek = await this.store.select(selectCopyingWeek).pipe(take(1)).toPromise();
		const selectedDates = await this.selectedDates$.pipe(take(1)).toPromise();
		this.store.dispatch(new PostCopyingWeek(copyingWeek, selectedDates[0]));
	}

	public async saveAsTemplate(): Promise<void> {
		const ref = this.dialog.open(ConfirmPopupComponent, {
			data: {
				title: this.interService.translate.instant('SHIFT_TEMPLATES.TEMPLATE'),
				description: this.interService.translate.instant('SHIFT_TEMPLATES.ENTER_A_NAME'),
				inputType: 'text',
				rightButtonType: EQOButtonType.BIG_RED,
				leftButtonText: this.interService.translate.instant('GLOBAL.CLOSE'),
				rightButtonText: this.interService.translate.instant('GLOBAL.ADD'),
			},
		});
		const response: { cancelled: boolean, value: string } = await ref.afterClosed().toPromise();
		if (!response || response.cancelled) {
			return;
		}
		this.store.dispatch(new CreateShiftTemplate({
				template_name: response.value,
				template_string: JSON.stringify(await this.getWeekObject()),
			},
			getCurrentState(this.store).session.department.id,
		));
	}

	public async createShiftDraft(): Promise<void> {
		const ref = this.dialog.open(ConfirmPopupComponent, {
			data: {
				title: this.interService.translate.instant('SHIFT_DRAFTS.DRAFT'),
				description: this.interService.translate.instant('SHIFT_DRAFTS.ENTER_A_NAME'),
				inputType: 'text',
				rightButtonType: EQOButtonType.BIG_RED,
				leftButtonText: this.interService.translate.instant('GLOBAL.CLOSE'),
				rightButtonText: this.interService.translate.instant('GLOBAL.ADD'),
			},
		});
		const response: { cancelled: boolean, value: string } = await ref.afterClosed().toPromise();
		if (!response || response.cancelled) {
			return;
		}
		this.store.dispatch(new CreateShiftDraft({
			name: response.value,
		}));
	}

	public chooseTemplate(): void {
		this.dialog.open(SelectShiftTemplatePopupComponent);
	}

	public chooseShiftDraft(): void {
		this.dialog.open(SelectShiftDraftPopupComponent);
	}

	public print(): void {
		this.store.dispatch(new SetPrinting(true));
		setTimeout(() => {
			printJS({
				printable: 'print-container',
				type: 'html',
				maxWidth: 1100,
				css: ['/assets/css/schedule.css'],
				targetStyles: ['*'],
				showModal: true,
				onLoadingEnd: () => this.store.dispatch(new SetPrinting(false)),
			});
		}, 200);
	}

	public ngOnInit(): void {
	}

	public ngOnDestroy(): void {
		this._subscriptions.forEach(s => s.unsubscribe());
	}

}
